'use client'

import Image from 'next/image'
import { useState } from 'react'
import { ORG_LOGO_PROXY_PATH } from '@/lib/org-logo'

const TILE = 'size-[100px] rounded-2xl border border-white/10 lg:size-28 xl:size-32'

/**
 * Organizer logo tile on /org/<slug>, with the initials tile as fallback.
 *
 * Optimized through next/image rather than hotlinked: Battlefy logos are raw
 * uploads of up to ~3 MB and 5000px for a tile that's at most 128 CSS px.
 * The fixed 128x128 box gives a 1x/2x srcset (128w / 256w WebP), which also
 * covers the 100px mobile tile at 2x-3x. `src` should come from orgLogoSrc()
 * so Challengermode logos arrive at 256px instead of 64px. Use
 * orgLogoTileSrc(): animated GIFs (which next/image passes through at full
 * weight) come back as /api/org-logo URLs, already a 256px animated WebP, so
 * they skip the optimizer.
 *
 * Client component only for `onError`: hotlinked images can disappear
 * upstream, and a broken-image icon is worse than the initials tile.
 */
export function OrgLogo({
  src,
  name,
  initials,
}: {
  src: string | null
  name: string
  initials: string
}) {
  const [failed, setFailed] = useState(false)

  if (!src || failed) {
    return (
      <div
        className={`${TILE} flex items-center justify-center bg-white/[0.04] font-space-mono text-3xl font-bold text-white/30`}
      >
        {initials}
      </div>
    )
  }

  return (
    // `priority` (not lazy): the logo is this page's LCP element and sits
    // above the fold, so lazy-loading leaves it blank in the first paint —
    // and in crawler and OG thumbnails.
    <Image
      src={src}
      alt={name}
      width={128}
      height={128}
      className={`${TILE} object-cover`}
      priority
      unoptimized={src.startsWith(ORG_LOGO_PROXY_PATH)}
      onError={() => setFailed(true)}
    />
  )
}
